import { Injectable, OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpResponse, HttpErrorResponse } from '@angular/common/http';

import { of, Observable, Subject, Subscription } from 'rxjs';
import { map, catchError, take, debounceTime, shareReplay } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { LoggingMessageService } from './logging-message.service';

import moment, { Moment } from 'moment';
// use date-fns package instead of Moment to greatly reduce package size

import { CookieService } from 'ngx-cookie-service';
import { DataResult } from '../models/data-result.mode';
import jwt_decode from 'jwt-decode';
import { SubscriptionChangeService } from './subscription-change.service';
import { TimeoutService } from './timeout-service.service';

@Injectable({
    providedIn: 'root'
})

export class AuthService implements OnInit, OnDestroy {
    private $subscriptions: Subscription = new Subscription();
    private FEDERATION_TOKEN_ID = environment.FederationTokenID;
    private ACCESS_TOKEN_ID = environment.AccessTokenID;
    private TOKEN_ID_EXPIRES = environment.TokenIDExpires;
    private TOKEN_REFRESH_PERIOD = environment.TokenRefreshPeriod;
    private TOKEN_EMAIL = environment.TokenEmail;
    private TOKEN_ROLE = environment.TokenUserRole;
    private TOKEN_USERHOSP_ID = environment.UserHospId;
    private TOKEN_SUBSCRIPTIONS_AVAILABLE = environment.TokenSubscriptionsAvailable;
    private SUBSCRIPTION_SELECTED = environment.SubscriptionSelected;
    private localExpiryDate: Moment;
    private user: string;
    private _NO_DATA = { data: [] };
    redirectUrl: string;
    private DEBOUNCE_TIME = 60000; // 1 min
    private userLoggedInSource = new Subject<boolean>();
    public userLoggedIn$ = this.userLoggedInSource.asObservable();
    private registerTokenRequestSource = new Subject<boolean>();
    public registerTokenRequestSource$ = this.registerTokenRequestSource.asObservable();
    private _isTimeoutProcessing = false;

    public get IsTimeoutProcessing(): boolean {
        return this._isTimeoutProcessing;
    }

    public set IsTimeoutProcessing(value: boolean) {
        this._isTimeoutProcessing = value;
    }

    constructor(private http: HttpClient,
                private logger: LoggingMessageService,
                private cookieService: CookieService,
                private timeoutService: TimeoutService,
                private subscriptionService: SubscriptionChangeService) {
        this.subscribeToTokenRequests();
    }

    ngOnInit(): void {
    }

    public publishLoginStatusUpdate(isLoggedIn: boolean) {
        setTimeout(() => {
            this.userLoggedInSource.next(isLoggedIn);
        }, 0);
    }

    public registerTokenRequest(tokenRequired: boolean) {
        setTimeout(() => {
            this.registerTokenRequestSource.next(tokenRequired);
            this.logger.info('auth.service', 'registerTokenRequest', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `registerTokenRequest TokenRequired:${tokenRequired}`, '');
        }, 0);
    }

    private subscribeToTokenRequests() {
        const sub = this.registerTokenRequestSource$
        .pipe(debounceTime(this.DEBOUNCE_TIME))
        .subscribe(() => {
            this.logger.info('auth.service', 'subscribeToTokenRequests', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,  'Requesting new token', '');
            this.getNewAccessToken(environment.TokenServiceApi, environment.TokenServiceMedia, environment.UseTestToken, environment.UseCookies, environment.CookieName);
        });
        this.$subscriptions.add(sub);
    }

    setAccessToken(token: any, useCookies: boolean) {
        try {
            var decoded = jwt_decode(token.token);

            const userEmail = this.getUserEmail(token);
            this.user = userEmail;
            sessionStorage.setItem(this.TOKEN_EMAIL, userEmail);

            const expiresAt = this.getAccessTokenExpiry(token);
            sessionStorage.setItem(this.TOKEN_ID_EXPIRES, expiresAt);
            const localExpiry = this.convertToLocalDateTime(expiresAt);
            this.localExpiryDate = localExpiry;
            console.log('auth.service - setAccessToken - localExpiryDate:', localExpiry, ' expiresAt:', expiresAt);
            this.logger.debug('auth.service', 'setAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Token expires:${expiresAt}`, '');

            const userRole = this.getUserRole(token);
            sessionStorage.setItem(this.TOKEN_ROLE, userRole);

            const userHospId = this.getUserHospId(decoded);
            sessionStorage.setItem(this.TOKEN_USERHOSP_ID, userHospId);

            const subs = this.getSubscriptionsAvailable(token);
            sessionStorage.setItem(this.TOKEN_SUBSCRIPTIONS_AVAILABLE, subs);
            this.subscriptionService.AvailableSubscriptions = subs.split(',').map(Number);

            // Only put token in session if Cookies aren't to be used, (for testing purposes)
            if (useCookies === false) {
                const tokenString = JSON.stringify(token.token);
                this.logger.debug('auth.service', 'setAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `TokenString:${tokenString}`, '');
                sessionStorage.setItem(this.ACCESS_TOKEN_ID, tokenString);
            }

        } catch(e: any) {
            this.logger.warn('auth.service', 'setAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `error decoding token:${e.message}`, '');
        }
    }

    setFederationToken(token: any) {
        const tokenString = JSON.stringify(token.Token);
        this.logger.debug('auth.service', 'setFederationToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `TokenString:${tokenString}`, '');
        sessionStorage.setItem(this.FEDERATION_TOKEN_ID, tokenString);
    }

    getSubscription(): string {
        const sub = sessionStorage.getItem(this.SUBSCRIPTION_SELECTED);
        return sub;
    }

    setSubscription(sub: string) {
        sessionStorage.setItem(this.SUBSCRIPTION_SELECTED, sub.toString());
    }

    logout() {
        this.deleteSessionVariables();
        this.publishLoginStatusUpdate(false);
    }

    private deleteSessionVariables() {
        sessionStorage.removeItem(this.FEDERATION_TOKEN_ID);
        sessionStorage.removeItem(this.ACCESS_TOKEN_ID);
        sessionStorage.removeItem(this.TOKEN_ID_EXPIRES);
        sessionStorage.removeItem(this.TOKEN_EMAIL);
        sessionStorage.removeItem(this.TOKEN_ROLE);
        sessionStorage.removeItem(this.TOKEN_USERHOSP_ID);
        sessionStorage.removeItem(this.TOKEN_SUBSCRIPTIONS_AVAILABLE);
        sessionStorage.removeItem(this.SUBSCRIPTION_SELECTED);
        this.localExpiryDate = null;
    }
    
    public isUserLoggedIn(): boolean {
        if (this.IsTimeoutProcessing) {
            return true;
        }
        const tokenHasExpired = this.isAccessTokenExpired();
        return !tokenHasExpired;
    }

    getAccessToken() {
        let token: any = {};
        const tokenString = sessionStorage.getItem(this.ACCESS_TOKEN_ID);
        this.logger.debug('auth.service', 'getAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `parsing token: ${tokenString}`, '');
        if (tokenString) {
            token = JSON.parse(tokenString);
        }
        return token;
    }

    getFederationToken() {
        let token: any = {};
        const tokenString = sessionStorage.getItem(this.FEDERATION_TOKEN_ID);
        this.logger.debug('auth.service', 'getFederationToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `parsing token: ${tokenString}`, '');
        if (tokenString) {
            token = JSON.parse(tokenString);
        }
        return token;
    }

    getUser(): string {
        const userEmail = this.user;
        return userEmail;
    }

    getTokenEmail(): string {
        const tokenEmail = sessionStorage.getItem(this.TOKEN_EMAIL);
        return tokenEmail;
    }

    getTokenRole(): string {
        const tokenRole = sessionStorage.getItem(this.TOKEN_ROLE);
        return tokenRole;
    }

    private getUserRole(token): string {
        const role = (token && token.role) ? token.role : '';
        return role;
    }

    private getUserHospId(token): string {
        const userhospid = (token && token.userhospid) ? token.userhospid : '';
        return userhospid;
    }

    private getUserEmail(token): string {
        const email = (token && token.email) ? token.email : '';
        return email;
    }

    getSubscriptionsAvailable(token): string {
        const subs = (token && token.subscriptionsavailable) ? token.subscriptionsavailable : '';
        return subs;
    }

    // private setSubscriptionsAvailable(token): string {
    //     const subs = (token && token.subscriptionsavailable) ? token.subscriptionsavailable : '';
    //     return subs;
    // }

    logTokenDetails(token: any, useCookies: boolean) {
        if (token) {
            const email = this.getUserEmail(token);
            // const role = this.getUserRole(token);
            // this.logMessage('INFO', email, `logTokenDetails - User logged in. Role:${role}`);
            if (useCookies === false) {
                this.logger.info('auth.service', 'logTokenDetails', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,  `Token: ${token.token}`, '');
            }
        }
    }

    isTokenStale() {
        const expiresAt = sessionStorage.getItem(this.TOKEN_ID_EXPIRES);
        if (expiresAt) {
            const expiryUtc = moment.utc(expiresAt, 'YYYY-MM-DD HH:mm:ss');
            const localExpiry = expiryUtc.local();
            const nowLocal = moment().local();
            const nowLocalPlusRefreshAge = nowLocal.add(this.TOKEN_REFRESH_PERIOD, 'minutes');
            const isTokenStale = nowLocalPlusRefreshAge.isAfter(localExpiry);
            if (isTokenStale === true) {
                const now = nowLocal.format('YYYY-MM-DD HH:mm:ss');
                const exiry = localExpiry.format('YYYY-MM-DD HH:mm:ss');
                const nowPlusRefresh = nowLocalPlusRefreshAge.format('YYYY-MM-DD HH:mm:ss');
                this.logger.warn('auth.service', 'isTokenStale', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Token is Stale - Now:${now}, NowPlusRefresh:${nowPlusRefresh}, Token Expiry:${exiry}`, '');
            }
            return isTokenStale;
        }
        return true;
    }

    private convertToLocalDateTime(datetime: string): Moment {
        const utc = moment.utc(datetime, 'YYYY-MM-DD HH:mm:ss');
        const local = utc.local();
        return local;
    }

    isAccessTokenExpired(): boolean {
        let localExpiry: Moment;

        if (this.localExpiryDate) {
            localExpiry = this.localExpiryDate;
        } else {
            const expiresAt = sessionStorage.getItem(this.TOKEN_ID_EXPIRES);
            if (expiresAt) {
                localExpiry = this.convertToLocalDateTime(expiresAt);
                this.localExpiryDate = localExpiry;
            }
        }

        if (localExpiry && localExpiry.isValid()) {
            const nowLocal = moment().local();
            const isTokenExpired = nowLocal.isAfter(localExpiry);
            if (isTokenExpired === true) {
                const now = nowLocal.format('YYYY-MM-DD HH:mm:ss');
                const exiry = localExpiry.format('YYYY-MM-DD HH:mm:ss');
                console.log('auth.service - isAccessTokenExpired. Token has expired. Now: ' + now + ', Token Expiry: ' + exiry);
                this.logger.warn('auth.service', 'isAccessTokenExpired', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Token Has Expired - Now: ${now}, Token Expiry: ${exiry}`, '');
                this.deleteSessionVariables();
            }
            return isTokenExpired;
        }
        return true;
    }

    getAccessTokenExpiry(token: any): string {
        let expires: string;
        if (token && token.expiresAt) {
            const expiresAt = moment(token.expiresAt);
            expires = expiresAt.format('YYYY-MM-DD HH:mm:ss');
        } else {
            const now = moment.utc().format('YYYY-MM-DD HH:mm:ss');
            expires = now;
        }
        this.logger.debug('auth.service', 'getAccessTokenExpiry', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,`expires:${expires}`, '');
        return expires;
    }

    public getNewAccessToken(tokenService: string, tokenMedia: string, useTestToken: boolean, useCookies: boolean, cookieName: string) {
        const storage = (useCookies === true) ? 'Cookie' : 'SessionStorage';
        // this.logger.info('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, 'Getting token', '');
        const refreshSubscription = this.refreshAccessToken(tokenService, tokenMedia, useTestToken, useCookies, cookieName)
            .subscribe(dataResult => {
                let token: any = {};
                if (dataResult && (dataResult.Ok === true)) {
                    // console.log("getNewAccessToken - Received fresh Token. Mapping....");
                    const tokenData: any = dataResult.Data;

                    token = this.mapAccessToken(tokenData);

                    this.logger.info('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Token mapped. ExpiresAt:${token.expiresAt}, Email:${token.email}, Role:${token.role}`, '');
                    if (token && token.expiresAt && token.email) {
                        this.logger.debug('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,`Setting token in: ${storage}`, '');

                        this.logger.info('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Setting Access Token, Email:${token.email}, Role:${token.role}`, '');
                        this.setAccessToken(token, useCookies);

                        // Successful token update. Timer can be reset
                        this.timeoutService.ResetTimeoutDialogTimer = true;

                        if (useCookies === true) {
                            const cookieExists: boolean = this.cookieService.check(cookieName);
                            if (cookieExists === true) {
                                this.logger.debug('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, 'Cookie Exists. Deleting Cookie', '');
                                this.cookieService.delete(cookieName, '/');
                            }
                            this.logger.debug('auth.service', 'getNewAccessToken',  this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, 'Creating new Cookie', '');
                            this.cookieService.set(cookieName, token.token, 1, '/', null, false, 'Strict');

                            this.logger.debug('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, 'Cookie set', '');
                        }

                        this.logger.debug('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Finished setting token in: ${storage}`, '');
                    } else {
                        // Token is Empty
                        this.logger.warn('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, 'TokenService returned token thats missing key information', '');
                        this.logger.warn('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Deleting token from : ${storage}`, '');
                        if (useCookies === true) {
                            this.logger.warn('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Deleting cookie : ${cookieName}`, '');
                            this.cookieService.delete(cookieName, '/');
                        }
                        this.deleteSessionVariables();
                    }
                } else if (dataResult && (dataResult.Status === 401)) {
                    // Unauthorized
                    // console.log("getNewAccessToken - Unauthorized");
                    this.logger.warn('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `TokenService returned 401 Unauthorized response`, '');
                    this.logger.warn('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Deleting token from : ${storage}`, '');
                    if (useCookies === true) {
                        this.logger.warn('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Deleting cookie : ${cookieName}`, '');
                        this.cookieService.delete(cookieName, '/');
                    }
                    this.deleteSessionVariables();
                } else if (dataResult && (dataResult.Status === 409)) {
                    // console.log("getNewAccessToken - Token conflict 409. Keep old token");
                    this.logger.warn('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `TokenService returned 409 Conflict response`, '');
                    this.logger.warn('auth.service', 'getNewAccessToken',  this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `User session exists, but key values are empty. Maybe repopulating. Keep using old token`, '');
                } else {
                    // Any other error
                    // console.log("getNewAccessToken - Other Token error. Keep old token");
                    this.logger.error('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `TokenService returned an error. Either service is unavailable or some internal error`, '');
                    this.logger.error('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `TokenService doesn't specifically say the user is unauthorized. Keep using old token`, '');
                }
                const isLoggedIn = this.isUserLoggedIn();
                // console.log("getNewAccessToken - publishLoginStatusUpdate: " + isLoggedIn);
                this.logger.info('auth.service', 'getNewAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `publishLoginStatusUpdate, Email:${token.email}, IsLoggedIn:${isLoggedIn}`, '');
                this.publishLoginStatusUpdate(isLoggedIn);
            });
        this.$subscriptions.add(refreshSubscription);
    }

    private refreshAccessToken(service: string, media: string, useTestToken: boolean, useCookies: boolean, cookieName: string): Observable<DataResult> {
        const webmethod = (useTestToken === true) ? '/TokenTest' : '/Token';
        // const media = 'application/json';
        const uri = `${service}${webmethod}`;
        this.logger.debug('auth.service', 'refreshAccessToken',  this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `calling url: ${uri}`, '');
        // const storage = (useCookies === true) ? 'Cookie' : 'SessionStorage';
        // const domain = environment.CookieDomain;
        // const path = environment.CookiePath;

        // headers: { Accept: media },
        return this.http.get<any>(
            uri,
            {
                observe: 'response'
            }
        )
            .pipe(
                map(response => {
                    let result: DataResult;
                    if (response instanceof HttpResponse) {
                        if (response.status === 200) {
                            result = new DataResult(response.ok, response.status, response.statusText, response.body);
                        } else if (response.status === 401) {
                            result = new DataResult(false, 401, response.statusText, this._NO_DATA);
                        } else if (response.status === 409) {
                            result = new DataResult(false, 409, response.statusText, this._NO_DATA);
                        } else {
                            result = new DataResult(false, 400, response.statusText, this._NO_DATA);
                        }
                    }
                    if (response instanceof HttpErrorResponse) {
                        result = new DataResult(false, 500, response.statusText, this._NO_DATA);
                    }
                    return result;
                }),
                catchError(error => {
                    this.logger.warn('auth.service', 'refreshAccessToken', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Error caught`, '');
                    const message = this.getErrorMessage(error);
                    return this.handleRefreshAccessTokenError(`refreshAccessToken failed`, message);
                }),
                take(1),
            );
    }

    private getErrorMessage(error: any): string {
        let message = error.message;
        if (!(message) && (error.error)) {
            message = error.error;
        }
        return message;
    }

    private mapAccessToken(resp: any): any {
        const accesstoken: any = {};
        if (resp) {
            accesstoken.token = resp.Token;
            accesstoken.expiresAt = resp.ExpiresAt;
            accesstoken.email = resp.Email;
            accesstoken.role = resp.Role;
            accesstoken.subscriptionsavailable = resp.SubscriptionsAvailable;
        }
        return accesstoken;
    }

    private handleRefreshAccessTokenError(operation: string, error: any) {
        this.logger.warn('auth.service', 'handleRefreshAccessTokenError', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Operation:${operation}`, `Error:${error}`);

        let result: any;
        if (error instanceof HttpErrorResponse) {
            const errorResponse = error as HttpErrorResponse;
            if (errorResponse && (errorResponse.status === 401)) {
                this.logger.warn('auth.service', 'handleRefreshAccessTokenError', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Operation:${operation}`, `401 Error:${error}`);
                const emptyResult = new DataResult(false, 401, error.message, this._NO_DATA);
                result = emptyResult;
            } else {
                this.logger.warn('auth.service', 'handleRefreshAccessTokenError', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Operation:${operation}`, `500 Error:${error}`);
                const emptyResult = new DataResult(false, 500, error.message, this._NO_DATA);
                result = emptyResult;
            }
        } else {
            this.logger.warn('auth.service', 'handleRefreshAccessTokenError', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Operation:${operation}`, `Server Error:${error}`);
            const emptyResult = new DataResult(false, 503, 'Service Unavailable error', this._NO_DATA);
            result = emptyResult;
        }
        return of(result);
    }

    unsubscribe() {
        if (this.$subscriptions) {
            this.$subscriptions.unsubscribe();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe();
    }
}
